import { CALL_API } from "constants/api";
import { OrderRecord } from "records/order";
import { add as onSnackbarAdd } from "./snackbars";
import Globalize from "globalize";
import { getCartQuery, getCartPricingQuery } from "queries/cart";
import { mutateAsync } from "lib/api";
import _ from "lodash";
import {
    CART_ITEM_REMOVE_FAILURE,
    CART_ITEM_REMOVE_REQUEST,
    CART_ITEM_REMOVE_SUCCESS,
    CART_ITEM_REQUEST,
    CART_ITEM_SUCCESS,
    CART_ITEM_FAILURE,
    CART_FAILURE,
    CART_PRICING_FAILURE,
    CART_REQUEST,
    CART_PRICING_REQUEST,
    CART_RETOGGLE,
    CART_SUCCESS,
    CART_PRICING_SUCCESS,
    CART_REDIRECT_AFTER_SUBMIT,
    CART_ITEM_ADD,
    CART_ITEM_REMOVE,
    CART_OPEN_CHECKOUT,
    SCOPE_SYNC_FROM_CART,
    SCOPE,
    WORKING_ORDER,
} from "constants/action-types";
import me from "models/me";
import { hasBuildOrderEnabled } from "components/build-order";
import { format } from "../lib/globalize-util";
import { fetchProductTemplate } from "./customization-actions";
import { gtm } from "helpers/gtm";

export const syncFromCart = (
    order,
    { syncToCart = false, companyError = false, updateRetailer = false, force = false } = {}
) => {
    return (dispatch, getState) => {
        const { scope, entities, ldFlags: flags } = getState();
        const buildOrderEnabled = hasBuildOrderEnabled({ me, scope, flags });
        const brandUsingStartOrder = scope.brand.hasPreference("use_start_order") && !buildOrderEnabled;
        const existingCompanyScope = scope.company;

        // TODO: remove this ASAP!
        // hide behind feature flag for now, plan is to remove after 1st month or so
        // Look, this exit hatch is required for proper behavior of View As.
        // At the same time, we need to unconditionally sync the cart from Grid View, therefore force.
        if (!brandUsingStartOrder && !force) {
            return;
        }

        // TODO: clean up the logic
        if (!_.isEmpty(order.retailer)) {
            const companyId = order.retailer.ref;
            if (entities.companies?.[companyId]) {
                updateCompany(entities.companies[companyId], order, syncToCart, dispatch);
            } else if (companyError) {
                dispatch({
                    type: CART_FAILURE,
                    error: "Company on order not found",
                });
                dispatch(
                    onSnackbarAdd({
                        message: Globalize.formatMessage(
                            "Company on order not found, please load cart to reload order"
                        ),
                    })
                );
            } else if (updateRetailer || brandUsingStartOrder) {
                dispatch({
                    type: SCOPE.COMPANIES.START,
                    payload: {
                        filters: {
                            __fields: "__all",
                            _id: [companyId],
                        },
                        meta: {
                            resumeCartSync: true,
                            replace: false,
                        },
                    },
                });
            }
        } else {
            const company = scope.accountType !== "buyer" ? null : existingCompanyScope;
            updateCompany(company, order, syncToCart, dispatch);
        }
    };
};

export const cartFetch = (options /*: { updateRetailer?: boolean, populate?: string[] }*/) => {
    return (dispatch, getState) => {
        const { scope } = getState();
        const { brand } = scope;

        if (brand) {
            const useAdvancedPricing = brand.isPricingPromotionEnabled(scope);

            dispatch({ type: CART_REQUEST });
            dispatch(mutateAsync(getCartQuery(brand._id, options))).then(({ transformed, status, body }) => {
                const isOk = status >= 200 && status < 300;
                if (isOk) {
                    if (useAdvancedPricing) {
                        body = _.omit(body, ["total"]);
                    }

                    const orderRecord = new OrderRecord(body);
                    const orderId = transformed._result;

                    dispatch({
                        type: CART_SUCCESS,
                        data: {
                            entities: {
                                orders: {
                                    [orderId]: orderRecord,
                                },
                            },
                            result: orderId,
                            orderedQuantities: body.__ordered_quantities,
                            isBulkOrder: body.__is_bulk_order,
                        },
                    });
                    dispatch(
                        syncFromCart(orderRecord, {
                            ...options,
                            syncToCart: false, // to not overwrite orderRecord by accident
                        })
                    );
                    if (useAdvancedPricing) {
                        dispatch(
                            cartPricingFetch({
                                ...options,
                                populate: _.union(options?.populate, ["__promotions", "__combine_promotions"]),
                            })
                        );
                    }
                } else {
                    dispatch({
                        type: CART_FAILURE,
                        error:
                            body && body.message
                                ? new Error(body.message)
                                : Globalize.formatMessage("Oops! Something went wrong. Please refresh and try again."),
                    });
                }
            });
        }
    };
};

export const cartPricingFetch = (options /*: { populate?: string[] }*/) => {
    return (dispatch, getState) => {
        const { scope } = getState();
        const { brand } = scope;
        dispatch({ type: CART_PRICING_REQUEST });
        dispatch(mutateAsync(getCartPricingQuery(brand._id, options))).then(({ transformed, status, body }) => {
            const { cart } = getState();
            const isOk = status >= 200 && status < 300;
            if (isOk) {
                const orderId = transformed._result;
                const orderRecord =
                    new OrderRecord({
                        ...cart?.entities.orders?.[orderId]?.toJS(),
                        total: body.total,
                    }) || new OrderRecord(body);
                dispatch({
                    type: CART_PRICING_SUCCESS,
                    data: {
                        entities: {
                            orders: {
                                [orderId]: orderRecord,
                            },
                        },
                        result: orderId,
                    },
                });
                if (brand.isPricingPromotionEnabled(scope)) {
                    // get promotions directly from body to prevent occuring promotions at order schema
                    dispatch(fetchPromotions(body));
                }
            } else {
                dispatch({
                    type: CART_PRICING_FAILURE,
                    error:
                        body && body.message
                            ? new Error(body.message)
                            : Globalize.formatMessage("Oops! Something went wrong. Please refresh and try again."),
                });
            }
        });
    };
};

export const cartItemAdd = (item, params = {}, snackOptions = {}) => {
    return (dispatch, getState) => {
        dispatch({
            type: CART_ITEM_ADD,
            payload: item._id,
        });
        item.addToOrder(params)
            .then(({ lineItems, rejectedItemsGroupedByCause }) => {
                dispatch(cartFetch());
                if (_.size(lineItems)) {
                    const [lastLineItem] = lineItems.slice(-1);

                    const templateId = lastLineItem?.__product_customizations_template_id;
                    if (templateId) {
                        dispatch(fetchProductTemplate(templateId));
                    }
                }

                if (!_.isEmpty(rejectedItemsGroupedByCause?.warehouse)) {
                    dispatch(
                        onSnackbarAdd({
                            message: format(
                                "|n| product(s) could not be added because they are not available in the selected warehouse.",
                                {
                                    n: _.size(rejectedItemsGroupedByCause.warehouse),
                                }
                            ),
                        })
                    );
                } else if (_.size(lineItems) && snackOptions.triggerSnackBar) {
                    const gtmCart = gtm.fetchCart(getState());
                    gtm.emit(
                        gtm.events.addToCart({
                            ...gtmCart,
                            send_to: gtm.groups.Brand,
                        })
                    );
                    dispatch(
                        onSnackbarAdd({
                            ...snackOptions,
                            message: params.inCart
                                ? Globalize.formatMessage("Product updated in cart")
                                : Globalize.formatMessage("Product added to cart"),
                            ...(params.isBuildOrderEnabled &&
                                snackOptions.message && { message: snackOptions.message }),
                        })
                    );
                }
            })
            .catch((error) => {
                dispatch(onSnackbarAdd({ message: error.message }));
                dispatch(cartFetch());
                dispatch({
                    type: CART_ITEM_REMOVE,
                    payload: item._id,
                });
            });
    };
};

export const cartItemRemove = (item, toast = true, snackOptions = {}) => {
    return (dispatch, getState) => {
        const { items } = getState().cart;
        if (items.has(item._id)) {
            dispatch({
                type: CART_ITEM_REMOVE,
                payload: item._id,
            });
            item.removeFromOrder()
                .then(() => {
                    dispatch(cartFetch());
                    if (toast) {
                        const gtmCart = gtm.fetchCart(getState());
                        gtm.emit(
                            gtm.events.removeFromCart({
                                ...gtmCart,
                                send_to: gtm.groups.Brand,
                            })
                        );
                        dispatch(
                            onSnackbarAdd({
                                ...snackOptions,
                                message: Globalize.formatMessage("Product removed from cart"),
                            })
                        );
                    }
                })
                .catch((error) => {
                    dispatch(onSnackbarAdd({ message: error.message }));
                });
        }
    };
};

export const cartBulkRemove = (itemIds) => cartRemoveItem(itemIds);

export const cartItemsFetch = (brandId) => {
    return (dispatch, getState) => {
        const state = getState();
        if (brandId || state.scope.brand) {
            dispatch({
                [CALL_API]: {
                    types: [CART_ITEM_REQUEST, CART_ITEM_SUCCESS, CART_ITEM_FAILURE],
                    endpoint: "/api/v2.0/orders/working/items",
                },
            });
        }
    };
};

const updateCompany = (company, order, syncToCart, dispatch) => {
    dispatch({
        type: SCOPE_SYNC_FROM_CART,
        payload: {
            company: company,
            currencyCode: order.currency_code,
            msrpCurrencyCode: order.msrp_currency_code,
        },
    });
    if (syncToCart) {
        const orderRecord = new OrderRecord({
            ...order,
            line_items: _.map(order.line_items, (li) => li?.product || li || ""),
        });
        const orderId = order._id;
        dispatch({
            type: CART_SUCCESS,
            data: {
                entities: {
                    orders: {
                        [orderId]: orderRecord,
                    },
                },
                result: orderId,
            },
        });
    }
};

export const cartRemoveItem = (itemIds) => {
    const ids = Array.isArray(itemIds) ? itemIds : [itemIds];
    return {
        [CALL_API]: {
            types: [CART_ITEM_REMOVE_REQUEST, CART_ITEM_REMOVE_SUCCESS, CART_ITEM_REMOVE_FAILURE],
            endpoint: "/api/v2.0/orders/working/line-items",
            method: "DELETE",
            query: { product: ids },
            meta: {
                itemIds: ids,
            },
        },
    };
};

export const fetchPromotions = ({ promotions }) => ({
    type: WORKING_ORDER.ORDER_PROMOTIONS.FETCH,
    payload: promotions,
});

export const cartRetoggle = (retoggle) => {
    return {
        payload: retoggle,
        type: CART_RETOGGLE,
    };
};

export const saveCartRedirectAfterSubmit = (location) => {
    return {
        type: CART_REDIRECT_AFTER_SUBMIT,
        payload: location,
    };
};

export const cartOpenCheckout = (open) => {
    return {
        type: CART_OPEN_CHECKOUT,
        payload: open,
    };
};
